import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { apiErrorMessage, useAuth } from "@/AuthContext";
import { LOGOUT } from "@/constants/testIds/auth";
import AdminNav from "@/pages/AdminNav";

const PAYMENT_OPTIONS = [
  { id: "moncash", label: "MonCash" },
  { id: "bank_transfer", label: "Bank Transfer" },
  { id: "other", label: "Other" },
  { id: "complimentary", label: "Complimentary" },
];

function formatWhen(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function sourceLabel(source) {
  if (source === "paddle_paid") return "Paddle paid";
  if (source === "paddle_trial") return "Paddle trial";
  if (source === "manual") return "Manual access";
  if (source === "admin") return "Admin";
  if (source === "blocked") return "Blocked";
  return "None";
}

function paymentLabel(method) {
  return PAYMENT_OPTIONS.find((item) => item.id === method)?.label || method || "—";
}

export default function AdminManualAccessPage() {
  const { logout } = useAuth();
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [method, setMethod] = useState("moncash");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [grantRequestId, setGrantRequestId] = useState(() => crypto.randomUUID());
  const [confirmGrantOpen, setConfirmGrantOpen] = useState(false);
  const [confirmRevokeOpen, setConfirmRevokeOpen] = useState(false);
  const [extendWarning, setExtendWarning] = useState("");

  const user = detail?.user;
  const history = detail?.history || [];
  const manualActive = user?.manual_access_active === true;

  const statusLines = useMemo(() => {
    if (!user) return [];
    return [
      ["Access", user.has_access ? "On" : "Off"],
      ["Access source", sourceLabel(user.access_source)],
      ["Manual expires", formatWhen(user.manual_access_expires_at)],
      ["Paddle status", user.paddle_subscription_status || detail?.paddle_subscription_status || "—"],
      ["Paid license", user.license_status || "inactive"],
      ["Blocked", user.is_blocked ? "Yes" : "No"],
    ];
  }, [detail, user]);

  async function loadUser(userId) {
    const res = await api.get(`/admin/manual-access/users/${userId}`);
    setDetail(res.data || null);
    setSelectedId(userId);
    setGrantRequestId(crypto.randomUUID());
    setExtendWarning("");
  }

  useEffect(() => {
    const handle = setTimeout(() => {
      const needle = query.trim();
      if (needle.length < 2) {
        setHits([]);
        return;
      }
      api.get("/admin/manual-access/users", { params: { q: needle } })
        .then((res) => setHits(res.data || []))
        .catch((err) => setError(apiErrorMessage(err)));
    }, 250);
    return () => clearTimeout(handle);
  }, [query]);

  async function grant({ confirmExtend = false } = {}) {
    if (!user?.id) return;
    setBusy("grant");
    setError("");
    try {
      const res = await api.post("/admin/manual-access/grant", {
        user_id: user.id,
        payment_method: method,
        payment_reference: reference.trim() || null,
        notes: notes.trim() || null,
        confirm: true,
        confirm_extend: confirmExtend,
        grant_request_id: grantRequestId,
      });
      setDetail(res.data || null);
      setExtendWarning("");
      setGrantRequestId(crypto.randomUUID());
      setConfirmGrantOpen(false);
    } catch (err) {
      if (err?.response?.status === 409) {
        setExtendWarning(apiErrorMessage(err) || "Manual access is already active.");
        setConfirmGrantOpen(true);
      } else {
        setError(apiErrorMessage(err));
        setConfirmGrantOpen(false);
      }
    } finally {
      setBusy("");
    }
  }

  async function revoke() {
    if (!user?.id) return;
    setBusy("revoke");
    setError("");
    try {
      const res = await api.post("/admin/manual-access/revoke", {
        user_id: user.id,
        notes: notes.trim() || null,
        confirm: true,
      });
      setDetail(res.data || null);
      setConfirmRevokeOpen(false);
    } catch (err) {
      setError(apiErrorMessage(err));
      setConfirmRevokeOpen(false);
    } finally {
      setBusy("");
    }
  }

  return (
    <div className="mp-shell mp-wide">
      <header className="mp-top">
        <div>
          <p className="mp-kicker">MinutePay</p>
          <h1>Manual Access</h1>
          <p className="mp-lead" style={{ marginBottom: 0 }}>
            Grant 30 days after you confirm an outside payment. This does not change Paddle subscriptions.
          </p>
        </div>
        <div className="mp-top-actions">
          <Button variant="outline" asChild>
            <Link to="/admin">Admin</Link>
          </Button>
          <Button data-testid={LOGOUT.button} variant="outline" onClick={logout}>
            Log out
          </Button>
        </div>
      </header>

      <AdminNav />
      {error ? <p className="mp-error">{error}</p> : null}

      <section className="mp-card mp-manual-panel">
        <h2>Find customer</h2>
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search by email"
          autoComplete="off"
        />
        <ul className="mp-manual-hits">
          {hits.map((hit) => (
            <li key={hit.id}>
              <button
                type="button"
                className={hit.id === selectedId ? "is-active" : undefined}
                onClick={() => loadUser(hit.id).catch((err) => setError(apiErrorMessage(err)))}
              >
                <strong>{hit.email}</strong>
                <span>{sourceLabel(hit.access_source)}</span>
              </button>
            </li>
          ))}
        </ul>
      </section>

      {user ? (
        <>
          <section className="mp-card">
            <h2>{user.email}</h2>
            <p className="mp-lead">{user.full_name || "No name on file"}</p>
            <dl className="mpd-dl">
              {statusLines.map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section className="mp-card">
            <h2>Grant 30 days</h2>
            <p className="mp-lead">Confirm the payment first. Access starts now and ends in exactly 30 days (UTC).</p>
            <div className="mp-manual-form">
              <label>
                Payment method
                <select value={method} onChange={(event) => setMethod(event.target.value)}>
                  {PAYMENT_OPTIONS.map((option) => (
                    <option key={option.id} value={option.id}>{option.label}</option>
                  ))}
                </select>
              </label>
              <label>
                Payment reference
                <Input value={reference} onChange={(event) => setReference(event.target.value)} placeholder="Optional" />
              </label>
              <label>
                Admin notes
                <Input value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Optional" />
              </label>
            </div>
            {extendWarning ? <p className="mp-error">{extendWarning}</p> : null}
            <div className="mp-top-actions" style={{ marginTop: 16 }}>
              <Button type="button" disabled={Boolean(busy)} onClick={() => setConfirmGrantOpen(true)}>
                Grant 30 Days
              </Button>
              {manualActive ? (
                <Button type="button" variant="outline" disabled={Boolean(busy)} onClick={() => setConfirmRevokeOpen(true)}>
                  Revoke manual access
                </Button>
              ) : null}
            </div>
          </section>

          <section className="mp-card">
            <h2>History</h2>
            <div className="mp-table-wrap">
              <table className="mp-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>When</th>
                    <th>Expires</th>
                    <th>Payment</th>
                    <th>Reference</th>
                    <th>Admin</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((row) => (
                    <tr key={row.id || row.created_at}>
                      <td>{row.action}</td>
                      <td>{formatWhen(row.created_at)}</td>
                      <td>{formatWhen(row.expires_at)}</td>
                      <td>{paymentLabel(row.payment_method)}</td>
                      <td>{row.payment_reference || "—"}</td>
                      <td>{row.admin_email || "—"}</td>
                      <td>{row.notes || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {history.length === 0 ? <p className="mp-empty">No manual access events yet.</p> : null}
            </div>
          </section>
        </>
      ) : null}

      <AlertDialog open={confirmGrantOpen} onOpenChange={setConfirmGrantOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{extendWarning ? "Extend manual access?" : "Confirm 30-day grant"}</AlertDialogTitle>
            <AlertDialogDescription>
              {extendWarning
                ? `${extendWarning} This adds a new 30-day window from now and will not shorten the current entitlement.`
                : `Grant ${user?.email || "this customer"} exactly 30 days of MinutePay access after confirming the ${paymentLabel(method)} payment? This does not create a Paddle subscription.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={Boolean(busy)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={Boolean(busy)}
              onClick={(event) => {
                event.preventDefault();
                grant({ confirmExtend: Boolean(extendWarning) });
              }}
            >
              {busy === "grant" ? "Granting…" : extendWarning ? "Confirm extend" : "Confirm grant"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmRevokeOpen} onOpenChange={setConfirmRevokeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke manual access?</AlertDialogTitle>
            <AlertDialogDescription>
              Remove the current 30-day manual grant for {user?.email || "this customer"}? Paddle access is not changed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={Boolean(busy)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={Boolean(busy)}
              onClick={(event) => {
                event.preventDefault();
                revoke();
              }}
            >
              {busy === "revoke" ? "Revoking…" : "Confirm revoke"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
